import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useBrowserBackButton } from '../../../hooks/useBrowserBackButton';

type ChangeTransportModalContextType = {
  closeModal: () => void;
  isOpen: boolean | undefined;
};

type ChangeTransportOpenModalContextType = {
  openModal: () => void;
};

const ChangeTransportModalContext = createContext<ChangeTransportModalContextType | undefined>(undefined);
const ChangeTransportOpenModalContext = createContext<ChangeTransportOpenModalContextType | undefined>(undefined);

export function ChangeTransportModalProvider(props: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  useBrowserBackButton({
    condition: isOpen,
    modalId: `transport-change-modal`,
    onClose: closeModal,
    onOpen: openModal,
  });

  const changeTransportDetailsModalMemoizedValue = useMemo(
    () => ({
      closeModal,
      isOpen,
    }),
    [closeModal, isOpen]
  );

  const changeTransportDetailsOpenModalMemoizedValue = useMemo(
    () => ({
      openModal,
    }),
    [openModal]
  );

  return (
    <ChangeTransportModalContext.Provider value={changeTransportDetailsModalMemoizedValue}>
      <ChangeTransportOpenModalContext.Provider value={changeTransportDetailsOpenModalMemoizedValue}>
        {props.children}
      </ChangeTransportOpenModalContext.Provider>
    </ChangeTransportModalContext.Provider>
  );
}

export function useChangeTransportModal() {
  const context = useContext(ChangeTransportModalContext);

  if (context === undefined) {
    throw new Error('useChangeTransportModal must be within a ChangeTransportModalProvider');
  }

  return context;
}

export function useChangeTransportOpenModal() {
  const context = useContext(ChangeTransportOpenModalContext);

  if (context === undefined) {
    throw new Error('useChangeTransportOpenModal must be within a ChangeTransportModalProvider');
  }

  return context;
}
